import { create } from 'zustand'
import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api'
import { disablePush } from '@/lib/webPush'
import { useCallStore } from '@/stores/callStore'
import { clearApiDataCache } from '@/lib/apiDataCache'
import { rememberWorkspaceName } from '@/lib/workspace'
import { checkOfflineIdentity, lockOfflineCrew } from '@/lib/offline/storage'

interface User {
  id: string
  workspaceId?: string
  email: string
  name: string
  /**
   * Structured name parts kept alongside the composed `name`. Consumers must
   * use these rather than splitting `name` on a space — that corrupts any
   * record whose first name contains one (see the Profile form regression).
   */
  firstName?: string
  lastName?: string
  role: 'owner' | 'admin' | 'sales' | 'support' | 'viewer' | 'member'
  avatar?: string
  mustResetPassword?: boolean
  shareCalendarTitles?: boolean
  /**
   * Server-granted capabilities from /api/auth/me (list or name → bool map).
   * When present, `can()` in lib/permissions.ts uses them instead of the role
   * ladder. Optional: older backends don't send it.
   */
  capabilities?: string[] | Record<string, boolean>
  /** Display name of a custom role, when the server sends one. */
  roleName?: string
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  loading: boolean
  mustResetPassword: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User) => void
  patchUser: (patch: Partial<User>) => void
  clearMustReset: () => void
  restoreSession: () => Promise<void>
}

function mapUser(raw: Record<string, unknown>): User {
  return {
    id: raw.id as string,
    workspaceId: typeof raw.workspaceId === 'string' ? raw.workspaceId : undefined,
    email: raw.email as string,
    name: (raw.name as string) ?? (`${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim() || (raw.email as string)),
    // Preserve the structured parts. Callers that need them (the Profile form)
    // must not re-derive them by splitting `name` — that silently corrupts any
    // record whose first name contains a space.
    firstName: typeof raw.firstName === 'string' ? raw.firstName : undefined,
    lastName: typeof raw.lastName === 'string' ? raw.lastName : undefined,
    role: ((raw.role as string)?.toLowerCase() ?? 'member') as User['role'],
    avatar: raw.image as string | undefined,
    mustResetPassword: raw.mustResetPassword as boolean | undefined,
    shareCalendarTitles:
      typeof raw.shareCalendarTitles === 'boolean' ? raw.shareCalendarTitles : undefined,
  }
}

/**
 * Copy app-level fields from the /api/auth/me payload onto the session user.
 * `capabilities` may sit on the user or beside it; accept either.
 */
function applyMeFields(user: User, data: Record<string, unknown> | undefined) {
  if (!data) return
  const dbUser = (data.user ?? {}) as Record<string, unknown>
  const caps = (dbUser.capabilities ?? data.capabilities) as User['capabilities'] | undefined
  if (caps && (Array.isArray(caps) || typeof caps === 'object')) user.capabilities = caps
  // F1: user.customRole { id, name, basedOn } wins over the built-in roleLabel.
  const customRole = (dbUser.customRole ?? null) as { name?: unknown } | null
  const roleName = customRole?.name ?? dbUser.roleName ?? dbUser.roleLabel ?? data.roleName ?? data.roleLabel
  if (typeof roleName === 'string' && roleName) user.roleName = roleName
  if (typeof dbUser.role === 'string' && dbUser.role) user.role = dbUser.role.toLowerCase() as User['role']
  // The business name rides along on /api/auth/me; cache it for the shell.
  const ws = dbUser.workspace as { id?: string | null; name?: string | null } | null | undefined
  if (typeof dbUser.workspaceId === 'string') user.workspaceId = dbUser.workspaceId
  else if (typeof ws?.id === 'string') user.workspaceId = ws.id
  if (ws && typeof ws === 'object') rememberWorkspaceName(ws)
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  token: null,
  user: null,
  loading: true,
  mustResetPassword: false,

  login: async (email: string, password: string) => {
    lockOfflineCrew()
    try {
      // The sign-in field takes an email or a username ("Gaige"); anything
      // without an "@" goes to Better Auth's username endpoint.
      const { data } = email.includes('@')
        ? await apiClient.post('/api/auth/sign-in/email', { email, password })
        : await apiClient.post('/api/auth/sign-in/username', { username: email, password })
      const user = mapUser(data.user)

      // Better Auth's session payload doesn't include app-specific fields
      // (mustResetPassword, shareCalendarTitles, …). /api/auth/me is the
      // authoritative source — merge those fields so downstream readers
      // (e.g. the calendar-privacy toggle in Settings → Availability) reflect
      // ground truth instead of defaults.
      let mustReset = user.mustResetPassword ?? false
      try {
        const meRes = await apiClient.get('/api/auth/me')
        const dbUser = meRes.data?.data?.user as Record<string, unknown> | undefined
        if (dbUser) {
          // The DB value wins: get-session can be up to 60s stale (cookie cache), so a
          // just-changed temporary password would otherwise bounce them back.
          if (typeof dbUser.mustResetPassword === 'boolean') mustReset = dbUser.mustResetPassword
          if (typeof dbUser.shareCalendarTitles === 'boolean') {
            user.shareCalendarTitles = dbUser.shareCalendarTitles
          }
          applyMeFields(user, meRes.data?.data)
        }
      } catch { /* ignore */ }

      set({
        isAuthenticated: true,
        token: null,
        user,
        mustResetPassword: mustReset,
      })
    } catch (err) {
      set({ isAuthenticated: false, token: null, user: null, mustResetPassword: false })
      throw err
    }
  },

  logout: async () => {
    lockOfflineCrew()
    // Theme 6: tear down Twilio Device on logout so the next login (or an
    // attacker sharing the browser) doesn't inherit a registered Device
    // bound to the previous user's identity. destroyDevice is a no-op
    // when no device is active.
    try {
      useCallStore.getState().destroyDevice()
    } catch { /* never let a callStore bug block sign-out */ }

    // Stop app notifications on this browser so the next person to sign in
    // here doesn't get the previous user's alerts. Must run before sign-out
    // (needs the session); capped so a slow network never blocks logout.
    try {
      await Promise.race([
        disablePush(),
        new Promise<void>(resolve => setTimeout(resolve, 1500)),
      ])
    } catch { /* push teardown is best-effort */ }

    try {
      // 3-second timeout prevents UI hang on dead/slow networks
      await Promise.race([
        apiClient.post('/api/auth/sign-out'),
        new Promise<void>(resolve => setTimeout(resolve, 3000)),
      ])
    } catch { /* ignore network errors — always clear local state */ }
    clearApiDataCache()
    set({ isAuthenticated: false, token: null, user: null, mustResetPassword: false })
  },

  setUser: (user: User) => { checkOfflineIdentity(user); set({ user }) },

  patchUser: (patch: Partial<User>) =>
    set((state) => { if (!state.user) return state; const user = { ...state.user, ...patch }; checkOfflineIdentity(user); return { user } }),

  clearMustReset: () => set({ mustResetPassword: false }),

  restoreSession: async () => {
    set({ loading: true })
    try {
      const { data } = await apiClient.get('/api/auth/get-session')
      if (data?.user) {
        const user = mapUser(data.user)

        // Same rationale as `login`: pull app-specific fields from /api/auth/me
        // so the in-memory user has shareCalendarTitles / mustResetPassword
        // populated. Without this the privacy toggle in Settings would always
        // start as `false` on page reload regardless of DB state.
        let mustReset = user.mustResetPassword ?? false
        try {
          const meRes = await apiClient.get('/api/auth/me')
          const dbUser = meRes.data?.data?.user as Record<string, unknown> | undefined
          if (dbUser) {
            // The DB value wins: get-session can be up to 60s stale (cookie cache), so a
            // just-changed temporary password would otherwise bounce them back.
            if (typeof dbUser.mustResetPassword === 'boolean') mustReset = dbUser.mustResetPassword
            if (typeof dbUser.shareCalendarTitles === 'boolean') {
              user.shareCalendarTitles = dbUser.shareCalendarTitles
            }
            applyMeFields(user, meRes.data?.data)
          }
        } catch { /* ignore */ }

        checkOfflineIdentity(user)
        set({
          isAuthenticated: true,
          token: null,
          user,
          mustResetPassword: mustReset,
          loading: false,
        })
        return
      }
    } catch (err) {
      if (isAxiosError(err) && !err.response) {
        // Temporary network failure — don't clear auth state or redirect to login
        set({ loading: false })
        throw err
      }
      // HTTP error (401 etc.) — no valid session; fall through to set loading: false
    }
    lockOfflineCrew()
    set({ loading: false })
  },
}))
